import React, { useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Project } from '../../types';
import { Popover } from '../ui/Popover';
import { StatusBadge, StatusDot, getStatusLabel, getStatusDescription } from '../ui/StatusBadge';

const STATUS_ORDER: Project['status'][] = ['bostad', 'värderad', 'planerad', 'väntar', 'avbruten', 'avslutad'];

interface StatusSelectProps {
  value: Project['status'];
  onChange: (status: Project['status']) => void;
  disabled?: boolean;
  /** Table-cell sizing. */
  compact?: boolean;
  /**
   * Called instead of onChange when the user picks "Klar för hämtning" and
   * the project has no pickup date yet. Lets the caller offer to set one.
   */
  hasPickupDate?: boolean;
  onNeedsPickupDate?: (status: Project['status']) => void;
  className?: string;
}

/**
 * Desktop status control: a badge that is visibly a dropdown, opening a
 * popover with every stage and what it means. Replaces the bottom sheet on
 * wide screens and the bare <select> on the form.
 */
export function StatusSelect({ value, onChange, disabled, compact = false, hasPickupDate = true, onNeedsPickupDate, className = '' }: StatusSelectProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const pick = (status: Project['status']) => {
    setOpen(false);
    if (status === value) return;
    if (status === 'värderad' && !hasPickupDate && onNeedsPickupDate) {
      onNeedsPickupDate(status);
      return;
    }
    onChange(status);
  };

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Status: ${getStatusLabel(value)}. Ändra status`}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-transparent transition-colors duration-150 hover:border-[var(--border-default)] hover:bg-[var(--bg-surface-hover)] disabled:opacity-50 disabled:cursor-not-allowed ${compact ? 'h-[32px] pl-0.5 pr-1.5' : 'h-[36px] pl-1 pr-2'} ${className}`}
      >
        <StatusBadge status={value} size={compact ? 'sm' : 'md'} />
        <ChevronDown size={14} className="text-[var(--text-secondary)]" aria-hidden="true" />
      </button>
      <Popover open={open} onClose={() => setOpen(false)} anchorRef={anchorRef} width={300} aria-label="Välj status">
        <ul role="listbox" className="py-1.5">
          {STATUS_ORDER.map(s => {
            const selected = s === value;
            return (
              <li key={s} role="option" aria-selected={selected} id={`status-opt-${s}`}>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); pick(s); }}
                  className={`w-full flex items-start gap-3 px-3.5 py-2 text-left transition-colors duration-150 hover:bg-[var(--bg-surface-hover)] ${selected ? 'bg-[var(--bg-surface-alt)]' : ''}`}
                >
                  <StatusDot status={s} className="mt-1.5" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-[var(--text-primary)]">{getStatusLabel(s)}</span>
                    <span className="block text-[13px] text-[var(--text-secondary)]">{getStatusDescription(s)}</span>
                  </span>
                  {selected && <Check size={16} className="mt-0.5 text-accent-600 dark:text-accent-400 flex-shrink-0" aria-hidden="true" />}
                </button>
              </li>
            );
          })}
        </ul>
      </Popover>
    </>
  );
}

export default StatusSelect;
